import type { ReactElement } from "react";
import { useEffect } from "react";

import type { IDropdownChild } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { isWorldwideCountry } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/helpers/countries";
import {
    CountryRadioEnum,
    CountryRadioSelectorEnum,
} from "app/pages/sales-intelligence/sub-modules/advanced-search/types/base";
import FilterRadioSelector from "app/pages/sales-intelligence/sub-modules/signals/components/shared/FilterRadioSelector/FilterRadioSelector";
import SignalFiltersWrapper from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalFiltersWrapper/SignalFiltersWrapper";
import { StyledDropdownLabelWrapped } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalsDropdownLabel/styles";
import { StyledInlineFilterWrapper } from "app/pages/sales-intelligence/sub-modules/signals/components/styles";
import useActiveFilterGenerator from "app/pages/sales-intelligence/sub-modules/signals/hooks/useActiveFilterGenerator";
import useCountryFilterData from "app/pages/sales-intelligence/sub-modules/signals/hooks/useCountryFilterData";
import useRestrictedCountries from "app/pages/sales-intelligence/sub-modules/signals/hooks/useRestrictedCountries";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import type { TrafficMetric } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficMetrics";
import type { TrafficNewMarketsDTO } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficNewMarketsDTO";
import type TrafficSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficSignalFilters";
import trackingService from "app/pages/sales-intelligence/sub-modules/signals/services/tracking/trackingService";
import type { ICountryObject } from "app/services/CountryService";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import FilterCountryListItem from "../../../../../../common-components/FilterCountryListItem/FilterCountryListItem";
import SignalMultiselectCountriesFilterTracked from "../../../../containers/SignalMultiselectCountriesFilter/SignalMultiselectCountriesFilterTracked";
import TrafficSignalTemplate from "../../../../models/traffic/TrafficSignalTemplate";
import type TrafficSignalFiltersViewProps from "../../../../models/view/traffic/TrafficSignalFiltersViewProps";
import FilterDropdownCountryItem from "../../../filters/shared/FilterDropdown/FilterDropdownCountryItem/FilterDropdownCountryItem";
import SignalTrafficChannelsFilter from "../../../filters/SignalTrafficChannelsFilter/SignalTrafficChannelsFilter";
import SignalTrafficThresholdFilter from "../../../filters/SignalTrafficThresholdFilter/SignalTrafficThresholdFilter";
import SignalDropdownLabel from "../../../shared/SignalsDropdownLabel/SignalDropdownLabel";

const signalI18nPrefix = "si.signals.management.traffic";

type Props = TrafficSignalFiltersViewProps & {
    countries: readonly ICountryObject[];
    newMarkets: Readonly<TrafficNewMarketsDTO>;
};

const NewMarketsFiltersView = (props: Props) => {
    const { signalAlias, filters, countries, newMarkets, onFiltersChange } = props;

    const {
        countriesWithoutWW,
        selectedRadio,
        setSelectedRadio,
        hasWWInCountries,
        isOnlyWW,
        preServingCountries,
        setPreServingCountries,
    } = useCountryFilterData({
        countries,
        newMarketCountries: filters.newMarkets?.countries,
    });

    const { currentActiveKey, setNextActiveKey } = useActiveFilterGenerator(filters.newMarkets);

    const { restrictedCountriesWithoutWW, restrictedCountryAction } = useRestrictedCountries();

    const availableRadioOptions: Record<string, boolean> = {
        [CountryRadioSelectorEnum.Specific]: countriesWithoutWW.length === 0,
        [CountryRadioSelectorEnum.Worldwide]: !hasWWInCountries,
    };

    const handleFilterChange = (updatedFilters: TrafficSignalFilters["newMarkets"]) => {
        onFiltersChange({
            newMarkets: {
                ...filters.newMarkets,
                ...updatedFilters,
            },
        });

        const shouldSetNextActiveKey =
            currentActiveKey && typeof filters.newMarkets?.[currentActiveKey] === "undefined";
        if (shouldSetNextActiveKey && !isOnlyWW) {
            setNextActiveKey();
        }
    };

    const handleChannelChange = (metric: TrafficMetric): void => {
        handleFilterChange({
            metric,
        });
    };

    const handleCountryChange = (countries: readonly number[]): void => {
        handleFilterChange({
            countries,
        });
    };

    useEffect(() => {
        if (countriesWithoutWW.length === 0 && hasWWInCountries) {
            handleCountryChange([WORLDWIDE_COUNTRY_ID]);
            const wwCountry = countries.find(isWorldwideCountry);
            if (wwCountry) {
                trackingService.filters.trackCountriesFilterChange([wwCountry.text], props.signalAlias);
            }
        }
    }, [countriesWithoutWW, countries]);

    const handleRadioSelect = (id: string) => {
        if (availableRadioOptions[id]) {
            return restrictedCountryAction();
        }

        let selectedCountries: number[] = [];

        if (id === CountryRadioEnum.worldwide) {
            if (filters.newMarkets?.countries && filters.newMarkets?.countries.length > 0) {
                setPreServingCountries(filters.newMarkets?.countries as number[]);
            }

            selectedCountries.push(WORLDWIDE_COUNTRY_ID);
            const wwCountry = countries.find(isWorldwideCountry);
            if (wwCountry) {
                trackingService.filters.trackCountriesFilterChange([wwCountry.text], props.signalAlias, "worldwide");
            }
        } else {
            selectedCountries = preServingCountries;
            setPreServingCountries([]);
        }

        handleCountryChange(selectedCountries);

        setSelectedRadio(id);
    };

    const handleThresholdChange = (thresholdValue: number): void => {
        handleFilterChange({
            thresholdValue,
        });
    };

    const renderCountriesOptions = (options: readonly ICountryObject[]): ReactElement[] => {
        const dropdownOptions = options.map(FilterDropdownCountryItem);

        if (restrictedCountriesWithoutWW.length === 0) {
            return dropdownOptions;
        }

        return dropdownOptions.concat(
            restrictedCountriesWithoutWW.map((country, index) => (
                <FilterCountryListItem
                    isFirstOfType={index === 0}
                    key={country.id}
                    country={country}
                    preventDefault
                    onClick={restrictedCountryAction}
                />
            )),
        );
    };

    const renderDropdownItems = (
        dropDownButton: ReactElement,
        selectedAllItem: IDropdownChild,
        items: readonly IDropdownChild[],
    ) => {
        if (selectedRadio !== CountryRadioSelectorEnum.Specific) {
            return [dropDownButton];
        }

        return [dropDownButton, selectedAllItem, ...items];
    };

    return (
        <SignalFiltersWrapper>
            <StyledDropdownLabelWrapped>
                {translate(`${signalI18nPrefix}.metrics.prefix.identify`)}
            </StyledDropdownLabelWrapped>
            <StyledInlineFilterWrapper>
                <SignalTrafficChannelsFilter
                    selectedValue={filters.newMarkets?.metric}
                    options={newMarkets.metrics}
                    autoOpen={currentActiveKey === "metric"}
                    onValueChange={handleChannelChange}
                />
            </StyledInlineFilterWrapper>

            <StyledDropdownLabelWrapped data-automation="traffic-metrics-postfix-text">
                {translate(`${signalI18nPrefix}.metrics.postfix`)}
            </StyledDropdownLabelWrapped>

            <StyledInlineFilterWrapper>
                <SignalDropdownLabel text={translate("si.signals.traffic.countryFrom.prefix")}>
                    <SignalMultiselectCountriesFilterTracked
                        filterKey={FilterType.Countries}
                        isAutoOpen={currentActiveKey === "countries"}
                        signalAlias={signalAlias}
                        selectedValue={filters.newMarkets?.countries}
                        renderDropdownItems={renderDropdownItems}
                        hasSearch={selectedRadio === CountryRadioSelectorEnum.Specific}
                        options={selectedRadio === CountryRadioSelectorEnum.Specific ? countriesWithoutWW : countries}
                        preContentComponent={
                            <FilterRadioSelector
                                selectedRadio={selectedRadio}
                                handleRadioSelect={handleRadioSelect}
                                availableRadioOptions={availableRadioOptions}
                            />
                        }
                        renderOptions={renderCountriesOptions}
                        onValueChange={handleCountryChange}
                    />
                </SignalDropdownLabel>
            </StyledInlineFilterWrapper>

            <StyledDropdownLabelWrapped>
                {translate(`${signalI18nPrefix}.traffic-threshold.prefix`)}
            </StyledDropdownLabelWrapped>
            <StyledInlineFilterWrapper>
                <SignalTrafficThresholdFilter
                    selectedValue={filters.newMarkets?.thresholdValue}
                    options={TrafficSignalTemplate.TRAFFIC_METRICS_THRESHOLD}
                    formatValue={abbrNumberFilter()}
                    onValueChange={handleThresholdChange}
                />
            </StyledInlineFilterWrapper>
            <StyledDropdownLabelWrapped data-automation="traffic-threshold-postfix-text">
                {translate(`${signalI18nPrefix}.traffic-threshold.postfix`)}
            </StyledDropdownLabelWrapped>
        </SignalFiltersWrapper>
    );
};

export default NewMarketsFiltersView;
